import type { Size } from './markupGeometry.ts';

export interface MarkupColor {
    id: string;
    label: string;
    // Canvas paint value: Tailwind classes can't reach a 2D context.
    value: string;
}

export type MarkupSize = 'small' | 'medium' | 'large';

interface MarkupSizeSpec {
    label: string;
    strokeFactor: number;
    fontFactor: number;
}

export const MARKUP_COLORS: readonly MarkupColor[] = [
    { id: 'red', label: 'Red', value: '#EF4444' },
    { id: 'yellow', label: 'Yellow', value: '#FACC15' },
    { id: 'green', label: 'Green', value: '#22C55E' },
    { id: 'blue', label: 'Blue', value: '#3B82F6' },
    { id: 'white', label: 'White', value: '#FFFFFF' },
    { id: 'black', label: 'Black', value: '#111827' }
];

export const DEFAULT_MARKUP_COLOR = MARKUP_COLORS[0];

// Sizes scale with the photo's long edge, so a mark reads the same on any resolution.
const SIZE_SPECS: Record<MarkupSize, MarkupSizeSpec> = {
    small: { label: 'Small', strokeFactor: 0.003, fontFactor: 0.026 },
    medium: { label: 'Medium', strokeFactor: 0.005, fontFactor: 0.036 },
    large: { label: 'Large', strokeFactor: 0.008, fontFactor: 0.05 }
};

export const MARKUP_SIZES: readonly MarkupSize[] = ['small', 'medium', 'large'];
export const DEFAULT_MARKUP_SIZE: MarkupSize = 'medium';

export const TEXT_FONT_FAMILY = 'Inter, ui-sans-serif, system-ui, sans-serif';
export const TEXT_FONT_WEIGHT = 700;
// Label box height per font size; leaves room for descenders under a 'top' baseline.
export const TEXT_LINE_HEIGHT = 1.2;

function longEdge(image: Size): number {
    return Math.max(image.width, image.height);
}

export function markupSizeLabel(size: MarkupSize): string {
    return SIZE_SPECS[size].label;
}

export function strokeWidthFor(size: MarkupSize, image: Size): number {
    return Math.max(1, longEdge(image) * SIZE_SPECS[size].strokeFactor);
}

export function fontSizeFor(size: MarkupSize, image: Size): number {
    return Math.max(8, longEdge(image) * SIZE_SPECS[size].fontFactor);
}

export function textFont(fontSize: number): string {
    return `${TEXT_FONT_WEIGHT} ${fontSize}px ${TEXT_FONT_FAMILY}`;
}
